import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Code, Plug, Gauge, Bot, ChevronDown, Sparkles, Wrench } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading";

// Skill categories data exactly matched to resume
const skillCategories = [
  {
    title: "AI & Modern Testing",
    icon: <Sparkles size={18} />,
    items: [
      "Claude",
      "GitHub Copilot",
      "Ollama",
      "RAG",
      "AI Skills",
      "AI Agents",
      "Playwright MCP",
      "AI-Assisted Testing",
      "Self-Healing Locator",
    ],
  },
  {
    title: "Programming Language",
    icon: <Code size={18} />,
    items: ["Java", "TypeScript"],
  },
  {
    title: "Automation",
    icon: <Bot size={18} />,
    items: [
      "Selenium",
      "Playwright",
      "Karate",
      "TestNG",
      "Manual Testing",
    ],
  },
  {
    title: "API Testing",
    icon: <Plug size={18} />,
    items: [
      "Karate",
      "Postman",
    ],
  },
  {
    title: "Performance Testing",
    icon: <Gauge size={18} />,
    items: [
      "JMeter",
    ],
  },
  {
    title: "Tools",
    icon: <Wrench size={18} />,
    items: [
      "Jira",
      "Azure Board",
      "GitHub",
      "Jenkins",
    ],
  },
];

const Skills = () => {
  const [selectedCategory, setSelectedCategory] = useState(skillCategories[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <section
      id="skills"
      className="pt-4 pb-10 px-4 sm:px-6 md:px-16 bg-white dark:bg-gray-900 transition-colors duration-300"
    >
      <div className="container mx-auto max-w-screen-xl">
        <SectionHeading
          title="Skills"
          subtitle="Technologies & tools I work with"
          gradient="blue"
        />

        {/* Mobile Category Modern Dropdown View (Visible only on mobile) */}
        <div className="md:hidden relative mb-4 z-20">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800/90 border border-blue-100 dark:border-gray-700 rounded-2xl shadow-sm hover:border-blue-300 dark:hover:border-blue-500 transition-all duration-300"
          >
            <div className="flex items-center gap-3.5">
              <span className="text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/40 p-2.5 rounded-xl border border-blue-100 dark:border-blue-800/30">
                {selectedCategory.icon}
              </span>
              <span className="font-bold text-gray-800 dark:text-gray-100 text-sm sm:text-base">
                {selectedCategory.title}
              </span>
            </div>
            <motion.div animate={{ rotate: isDropdownOpen ? 180 : 0 }} className="bg-gray-50 dark:bg-gray-700/50 p-1.5 rounded-lg">
              <ChevronDown className="text-gray-500 dark:text-gray-400" size={18} />
            </motion.div>
          </button>

          <AnimatePresence>
            {isDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.98 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="absolute top-full left-0 right-0 mt-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-xl overflow-hidden z-30"
              >
                {skillCategories.map((cat, idx) => {
                  const isActive = selectedCategory.title === cat.title;
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        setSelectedCategory(cat);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-3.5 px-4 py-3 transition-all duration-300 ${isActive
                          ? "bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500"
                          : "hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-4 border-transparent"
                        }`}
                    >
                      <span className={`transition-colors duration-300 ${isActive ? "text-blue-500" : "text-gray-400 dark:text-gray-500"}`}>
                        {cat.icon}
                      </span>
                      <span className={`font-semibold text-sm transition-colors duration-300 ${isActive ? "text-blue-700 dark:text-blue-300" : "text-gray-600 dark:text-gray-300"}`}>
                        {cat.title}
                      </span>
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Desktop Floating Horizontal Pill Tabs */}
        <div className="hidden md:flex flex-wrap justify-center gap-3 md:gap-4 mb-6">
          {skillCategories.map((cat, idx) => {
            const isActive = selectedCategory.title === cat.title;
            return (
              <button
                key={idx}
                onClick={() => setSelectedCategory(cat)}
                className={`flex items-center gap-2.5 px-5 py-3 rounded-full font-medium transition-all duration-300 border
                  ${isActive
                    ? "bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-500/25 scale-105"
                    : "bg-gray-50 border-gray-200 dark:bg-gray-800/80 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:-translate-y-0.5"
                  }
                `}
              >
                <span className={`transition-colors duration-300 ${isActive ? "text-white" : "text-blue-500 dark:text-blue-400"}`}>
                  {cat.icon}
                </span>
                <span className="whitespace-nowrap text-base">
                  {cat.title}
                </span>
              </button>
            );
          })}
        </div>

        {/* Dynamic Canvas (IDE Context Bar Style) */}
        <div className="min-h-[280px] max-w-4xl mx-auto w-full pt-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedCategory.title}
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 15 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex flex-col md:flex-row gap-6 md:gap-8 items-start relative pl-6 md:pl-8 before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-blue-500 before:rounded-full"
            >

              {/* Left Side: Minimalist VS Code like Context Label */}
              <div className="flex-shrink-0 flex items-center md:items-start gap-4 md:flex-col md:w-52 text-left mt-1">
                <span className="text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 p-2.5 rounded-lg border border-blue-100 dark:border-blue-800/30">
                  {selectedCategory.icon}
                </span>
                <div className="flex flex-col">
                  <span className="text-[11px] font-mono tracking-widest text-gray-400 dark:text-gray-500 uppercase mb-0.5">
                  </span>
                  <h3 className="text-sm md:text-base font-bold text-gray-800 dark:text-gray-200 leading-snug">
                    {selectedCategory.title}
                  </h3>
                </div>
              </div>

              {/* Right Side: Tag Sequence */}
              <div className="flex flex-wrap gap-3 sm:gap-4 flex-1">
                {selectedCategory.items.map((item, idx) => (
                  <motion.span
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: idx * 0.04 }}
                    className="px-4 py-2 text-sm sm:text-base font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-sm hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-600 dark:hover:text-blue-400 transition-colors duration-300 cursor-default"
                  >
                    {item}
                  </motion.span>
                ))}
              </div>

            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </section>
  );
};

export default Skills;